import type { DatasetResource } from "@prequel/import-react";
import {
  Button,
  ButtonStyle,
  Dropdown,
  type DropdownListItem,
  FormField,
  SectionExpandable,
  Spinner,
  Toggle,
} from "@prequel/shared-components";

import { Divider, FormFooter, FormSection, ReadOnlyFormValue } from "@/components/import/FormLayout";

type StepEditProps = {
  dataset: DatasetResource;
  isEnabled: boolean;
  name: string;
  onCancel: () => void;
  onFrequencyChange: (frequencyMinutes: string) => void;
  onNameChange: (value: string) => void;
  onSetEnabled: (isEnabled: boolean) => void;
  selectedFrequency: DropdownListItem<string>;
  syncFrequencyItems: DropdownListItem<string>[];
  updateDatasetPending: boolean;
};

const StepEdit = ({
  dataset,
  isEnabled,
  name,
  onCancel,
  onFrequencyChange,
  onNameChange,
  onSetEnabled,
  selectedFrequency,
  syncFrequencyItems,
  updateDatasetPending,
}: StepEditProps) => (
  <>
    <FormSection title="Name the dataset">
      <FormField
        id="dataset-name"
        label="Name"
        type="text"
        value={name}
        onChangeHandler={onNameChange}
        disabled={updateDatasetPending}
        subtext="A descriptive name for this dataset."
      />
    </FormSection>

    <Divider />

    {/* Source data is fixed once a dataset exists, so editing only shows what it reads. */}
    <FormSection title="Source data">
      <div className="space-y-6">
        <ReadOnlyFormValue label="Source ID" value={dataset.source_id} />
        <ReadOnlyFormValue label="Method" value={dataset.method} />
        {dataset.method === "table" ? (
          <ReadOnlyFormValue
            label="Table"
            value={`${dataset.table.source_table_namespace}.${dataset.table.source_table_name}`}
          />
        ) : (
          dataset.method === "sql" && <ReadOnlyFormValue label="Query" value={dataset.sql.query} />
        )}
        <ReadOnlyFormValue label="Type" value={dataset.type} />
      </div>
    </FormSection>

    <Divider />

    <FormSection title="Sync Frequency & Advanced Options">
      <Dropdown
        label="Sync Frequency"
        items={syncFrequencyItems}
        selectedItem={selectedFrequency}
        setSelectedItem={(item: DropdownListItem<string>) => onFrequencyChange(item.key)}
      />
      <SectionExpandable label="Advanced options" transparent>
        <Toggle
          label="Enabled"
          checked={isEnabled}
          setChecked={onSetEnabled}
          subtext="When enabled, this dataset will be synced on its configured schedule."
        />
      </SectionExpandable>
    </FormSection>

    <FormFooter>
      <Button type={ButtonStyle.TERTIARY} text="Cancel" onClick={onCancel} disabled={updateDatasetPending} />
      <Button
        type={ButtonStyle.PRIMARY}
        submit
        disabled={updateDatasetPending}
        text={
          updateDatasetPending ? (
            <span className="flex items-center">
              <Spinner.Inline className="mr-3 text-white" />
              Saving...
            </span>
          ) : (
            "Save Dataset"
          )
        }
      />
    </FormFooter>
  </>
);

export default StepEdit;
